/* eslint-disable no-console */
/* eslint-disable max-len */
import { Component, Prop, Ref, SimpleEvent, Watch } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { CartMain } from "../Cart/CartMain";
import { formatMoney } from '@/utils/formatting';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { QuantitySelector } from '@/components/QuantitySelector/QuantitySelector';
import { SideCart } from '../SideCart/SideCart';
import { throttle } from 'lodash';
import { scrollToElement } from '@/utils/scroll';

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop defaultVariant!: Shopify.Liquid.Variant;
    @Prop optionStyle!: 'dropdown'|'buttons';

    @Ref({required: false}) quantitySelector!: QuantitySelector;
    @Ref({required: false}) options!: ProductOptionPicker;
    @Ref({required: false}) actions!: HTMLDivElement;
    @Ref({required: false}) submitButton!: HTMLButtonElement;
    @Ref error!: HTMLElement;
    @Ref errorMessage!: HTMLElement;
    @Ref subscriptionWrapper!: HTMLElement;
    @Ref({required: false}) wishlist!: HTMLButtonElement;
    @Ref({ required: false }) stickyBanner!: HTMLElement;
    @Ref({ required: false }) stickyBannerProductOptions!: HTMLElement;
    @Ref({ required: false }) stickySubmitButton!: HTMLElement;
    @Ref({ required: false }) editButton!: HTMLElement;

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    protected quantity: number = 1;
    protected frequency:string|null = '';
    private cartMain:CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart:SideCart|null = document.body.querySelector('side-cart');
    private mobileBreakpoint: number = 1024;
    private siteMenu: HTMLElement|null = document.querySelector('.site-header');
    private siteFooter: HTMLElement|null = document.querySelector('.site-footer');

    /**
     * Initiaze compoenent.
     */
    init() {
        // Set default variant
        this.selectedVariant = this.defaultVariant;
        this.checkVariantAvailability(this.selectedVariant);
        this.setEventListeners();
        this.addStickyBannerEventListeners();

        //Removing variant query param from none product pages
        const currentPageUrl: string = window.location.href;
        const wordToCheck: string = "products";
    }

    /**
     * Set event listeners.
     */
    protected setEventListeners() {
        const subscription = this.submitButton?.getAttribute('is_subscription');

        // Listen for quantity updates
        if (this.quantitySelector) {
            this.$subscribe(this.quantitySelector.onChange, (quantity) => {

                // Hide error
                this.error.classList.remove('product-form__error--active');

                // Update quantity
                this.quantity = quantity;
            });
        }

        if (subscription === 'true') {
            this.frequency = this.submitButton?.getAttribute('frequency');
            this.initObserver();
        }

        if (this.wishlist) {
            this.$listen(this.wishlist, 'click', () => {
                // Wait for Wishlist to load
                setTimeout(()=> {
                    document.dispatchEvent(new Event('wishlistUpdated'));
                }, 500);

            });
        }

        this.listenOptionsSelector();
        this.listenSubmitClick();
        this.listenStickySubmitClick();

        document.dispatchEvent(new Event('cartChanged'));
    }

    /**
     * Initialize subscription observer
     */
    initObserver() {
        const observer = new MutationObserver(() => {
            // eslint-disable-next-line max-len
            const plansDropdown: HTMLSelectElement|null = this.subscriptionWrapper.querySelector('[data-plans-dropdown]');
            const rechargeRadios = this.subscriptionWrapper.querySelectorAll('.rc_widget__option__input');
            const sellingPlans: Shopify.Liquid.SellingPlan[] = this.product.selling_plan_groups[0].selling_plans as Shopify.Liquid.SellingPlan[];

            if (plansDropdown) {
                this.$listen(plansDropdown, 'change', () => {
                    for (const plan of sellingPlans) {
                        if (plan.id == Number(plansDropdown.value)) {
                            this.frequency = plan.name as string;
                        }
                    }
                });
            }

            for (const radio of rechargeRadios) {
                const inputRadio = radio as HTMLInputElement;
                this.updateSubscriptionPrice(inputRadio);

                this.$listen(radio as HTMLInputElement, 'change', () => {
                    this.updateSubscriptionPrice(inputRadio);
                });
            }
        });
        observer.observe(this.subscriptionWrapper, {
            subtree: true,
            childList: true
        });
    }

    updateSubscriptionPrice(radio:HTMLInputElement) {
        if (!radio) { return; }
        const priceContainer = document.querySelector('.product-main-details__price--normal');
        const finalPriceContainer = document.querySelector('.product-main-details__price--final');
        const subPriceWrapper = radio.parentElement?.querySelector('.rc_widget__price') as HTMLElement;

        if (radio.checked) {
            if (finalPriceContainer) {
                finalPriceContainer.innerHTML = `
                    ${subPriceWrapper?.textContent} <span class="body-2">(${this.frequency})</span>
                `;

                if (radio.classList.contains('rc_widget__option__input--subsave')) {
                    finalPriceContainer.classList.remove('visually-hidden');
                } else {
                    finalPriceContainer.classList.add('visually-hidden');
                }
            }

            if (priceContainer) {
                if (radio.classList.contains('rc_widget__option__input--subsave')) {
                    priceContainer.classList.add('strikethrough');
                } else {
                    priceContainer.classList.remove('strikethrough');
                }
            }
        }
    }

    /**
     * Listen to option change.
     */
    protected listenOptionsSelector() {
        const subscription = this.submitButton?.getAttribute('is_subscription');
        const sellingPlan:string|null = this.submitButton?.getAttribute('selling_plan');
        // Listen for variant updates
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
                if (variant === null) {
                    return;
                }
                this.selectedVariant = variant;

                // Check variant availability
                this.checkVariantAvailability(this.selectedVariant);

                // Update price
                const priceContainer = document.querySelector('.product-main-details__price--normal');
                if (priceContainer) {
                    if (variant.compare_at_price) {
                        priceContainer.textContent = formatMoney(variant.compare_at_price) as string;
                    } else {
                        priceContainer.textContent = formatMoney(variant.price) as string;
                    }
                }
                const finalPriceContainer = document.querySelector('.product-main-details__price--final');
                if (finalPriceContainer) {
                    if (variant.compare_at_price) {
                        finalPriceContainer.textContent = formatMoney(variant.price) as string;
                        finalPriceContainer.classList.remove('visually-hidden');

                        if (priceContainer) {
                            priceContainer.classList.add('strikethrough');
                        }
                    } else {
                        finalPriceContainer.classList.add('visually-hidden');

                        if (priceContainer) {
                            priceContainer.classList.remove('strikethrough');
                        }
                    }
                }

                if (subscription === 'true') {
                    if (sellingPlan) {
                        // eslint-disable-next-line max-len
                        const sellingPlanRadio:HTMLInputElement|null = document.querySelector('[data-radio-subsave]') as HTMLInputElement;
                        this.updateSubscriptionPrice(sellingPlanRadio);
                    } else {
                        const onetimePrice:HTMLElement|null = document.querySelector('#rc_price_onetime');
                        const autodeliverPrice:HTMLElement|null = document.querySelector('#rc_price_autodeliver');
                        const subscriptionsString = this.submitButton?.getAttribute('subscriptions_json');
                        let subscriptionPrice = 0;
                        if (subscriptionsString) {
                            const json = JSON.parse(String(subscriptionsString));
                            subscriptionPrice = json[variant.id].discount_variant_price;
                        }


                        if (onetimePrice?.parentElement?.querySelector('.newPrice') == null) {
                            const onetimeSpan =  document.createElement('span');
                            onetimeSpan.classList.add('newPrice');

                            if (onetimePrice === null) { return; }
                            onetimePrice.parentElement?.append(onetimeSpan);
                        }

                        if (autodeliverPrice?.parentElement?.querySelector('.newPrice') == null) {
                            const autodeliverSpan =  document.createElement('span');
                            autodeliverSpan.classList.add('newPrice');

                            if (autodeliverPrice === null) { return; }
                            autodeliverPrice.parentElement?.append(autodeliverSpan);
                        }

                        onetimePrice.style.display = 'none';
                        // eslint-disable-next-line max-len
                        const onetimeHasNewPrice = onetimePrice.parentElement? onetimePrice.parentElement.querySelector('.newPrice'): undefined;
                        if (onetimeHasNewPrice == null ) { return; }
                        onetimeHasNewPrice.innerHTML = formatMoney(variant.price) as string;


                        autodeliverPrice.style.display = 'none';
                        const autodeliverHasNewPrice = autodeliverPrice.parentElement?.querySelector('.newPrice');
                        if (autodeliverHasNewPrice == null ) { return; }
                        autodeliverHasNewPrice.innerHTML = formatMoney(subscriptionPrice) as string;
                    }
                }

                // Update sticky banner product options
                this.updateStickyBannerProductOptions();

                // Hide error
                this.error.classList.remove('product-form__error--active');
            });
            this.selectedVariant = this.options.selectedVariant;
        }
    }

    private updateStickyBannerProductOptions() {

        // Ensure element and selected variant exists
        if (!this.stickyBannerProductOptions || this.selectedVariant === null) {
            return;
        }

        // Loop through options, update text of selected option data
        let count = 1;
        for (const option of this.selectedVariant.options) {

            // Get nth child
            const currentStickyBannerOptionData = this.stickyBannerProductOptions.querySelector(`.product-sticky__option:nth-child(${count}) .product-sticky__option-data`);
            if (currentStickyBannerOptionData == null) {
                return;
            }

            // Update text content
            currentStickyBannerOptionData.textContent = option;

            // Increment count
            count++;
        }
    }

    /**
     * Listen for submit button click.
     */
    protected listenSubmitClick() {
        const subscription = this.submitButton?.getAttribute('is_subscription');

        // Listen for form submission
        if (this.submitButton) {
            this.$listen(this.submitButton, 'click', () => {

                const properties: {[key: string]: string} = {};
                let selected_value = 'onetime';

                if (subscription === 'true') {
                    /* eslint-disable-next-line */
                    let subscription_selected:HTMLInputElement|null = this.querySelector('input[name="purchase_type"]:checked');

                    if (!subscription_selected) {
                        subscription_selected = this.querySelector('input[name*="selling_plan_"]:checked');
                    }

                    if (subscription_selected !== null) {
                        selected_value = subscription_selected.value;
                    }

                    if (selected_value == 'autodeliver' || selected_value == 'subsave') {
                        this.checkRechargeVersions(properties);
                        return;
                    }
                }

                this.addToCart(this.selectedVariant?.id, properties, null).catch((e) => {
                    this.$log.error(`Add to cart failed`, { e });
                });
            });
        }
    }

    /**
     * Listen for submit button click on sticky banner
     */
    protected listenStickySubmitClick() {
        // Listen for form submission
        if (this.stickySubmitButton) {
            this.$listen(this.stickySubmitButton, 'click', () => {
                const properties: {[key: string]: string} = {};

                if (this.stickySubmitButton.classList.contains('product-sticky__submit--disabled')) {
                    scrollToElement(this as HTMLElement);
                } else {
                    this.addToCart(this.selectedVariant?.id, properties, null).catch((e) => {
                        this.$log.error(`Add to cart failed`, { e });
                    });
                }
            });
        }
    }

    /**
     * Check ReCharge Payments App Version.
     * - Background: In November 2020 Recharge updated the app to use shopify plans
     * instead of a separate recharger chechout. The implementation is slightly different
     * if the App was installed before/after November 2020.
     * - The below script checks for which implementation to use (rechargeCheckout or shopifyPlans)
     * - Learn more: https://support.rechargepayments.com/hc/en-us/articles/360008681834
     * @param value - selected value.
     * @returns
     */
    protected checkRechargeVersions(properties: {[key: string]: string}) {
        const sellingPlan:string|null = this.submitButton?.getAttribute('selling_plan');
        if (!sellingPlan) {
            this.handleCartItemProperties(properties, false, null);
            return;
        }

        this.handleCartItemProperties(properties, true, sellingPlan);
    }

    /**
     * Handle cart item properties, then send cart data.
     * @param properties - product properties.
     * @param isSellingPlan - check if it has selling plan
     * @param sellingPlan - selling plan id.
     * @returns
     */
    handleCartItemProperties(properties: {[key: string]: string}, isSellingPlan:boolean, sellingPlan:string|null) {
        let frequency = '';
        let unit_type:string|null = this.submitButton?.getAttribute('unit_type');

        if (this.frequency === null) {
            frequency = '';
        }

        frequency = this.frequency as string;

        if (unit_type === null) {
            unit_type = '';
        }

        /* eslint-disable */
        properties = {
            'shipping_interval_frequency': frequency,
            'shipping_interval_unit_type': unit_type,
        };
        /* eslint-enable */

        if (isSellingPlan) {
            /* eslint-disable */
            properties = {
                '_shipping_interval_frequency': frequency,
                '_shipping_interval_unit_type': unit_type,
            };
            /* eslint-enable */

            this.addToCart(this.selectedVariant?.id, properties, sellingPlan).catch((e) => {
                this.$log.error(`Add to cart failed`, { e });
            });
            return;
        }

        this.checkVariantId(properties);
    }

    /**
     * Check if adding subscription product and update variant id accordingly.
     * - This method is for recharge checkout implementation.
     * @param properties - product properties.
     * @returns
     */
    protected checkVariantId(properties: {[key: string]: string}) {
        const variant:number|undefined = this.selectedVariant?.id;

        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        if (!this.submitButton) { return; }
        const subscriptionsString = this.submitButton?.getAttribute('subscriptions_json');
        const json = JSON.parse(String(subscriptionsString));
        const updatedVariant = json[variant].discount_variant_id;

        this.addToCart(updatedVariant, properties, null).catch((e) => {
            this.$log.error(`Add to cart failed`, { e });
        });
    }

    /**
     * Show error
     * @param msg - error message.
     */
    protected showError(msg: string|null): void {
        this.errorMessage.textContent = msg;
        if (msg) {
            this.error.classList.add('product-form__error--active');
        } else {
            this.error.classList.remove('product-form__error--active');
        }
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        if (!document.body.classList.contains('template-product')) { return; }
    }

    @Watch('loading')
    protected handleLoading() {
        if (this.submitButton) {
            this.submitButton.disabled = this.loading;
        }
    }

    private _onSelectedPropertyChange = new SimpleEvent<string>();
    public get onSelectedPropertyChange() {
        return this._onSelectedPropertyChange.asEvent();
    }

    /**
     * Check if variant is available.
     * @param variant
     * @returns
     */
    checkVariantAvailability(variant: Shopify.Liquid.Variant|null) {
        // Check variant is defined
        if (variant === null || variant === undefined) {
            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Check variant is in stock
        if (!variant.available) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Enable
        this.updateActionsVisibility(false);
    }

    updateActionsVisibility(disabled: boolean) {
        if (disabled) {
            // Submit button
            if (this.submitButton) {
                // Disable button
                this.submitButton.classList.add('product-form__submit--disabled');
            }
            // Sticky Submit button
            if (this.stickySubmitButton) {
                // Disable button
                this.stickySubmitButton.classList.add('product-sticky__submit--disabled');
            }
        } else {
            // Submit button
            if (this.submitButton) {
                // Enable button
                this.submitButton.classList.remove('product-form__submit--disabled');
            }
            // Sticky Submit button
            if (this.stickySubmitButton) {
                // Enable button
                this.stickySubmitButton.classList.remove('product-sticky__submit--disabled');
            }
        }
    }

    /**
     * Add product to cart.
     * @param variant
     * @param properties
     * @returns
     */
    protected async addToCart(variant:number|undefined, properties: {[key: string]: string}, sellingPlan:string|null) {
        this.loading = true;

        // Check variant exists
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        // Define selling planFsselecte
        const sanitizedPlan = sellingPlan?Number(sellingPlan): undefined;
        const itemInCart = this.cartMain.checkCart(variant);

        // Add to cart
        cart.addItem({
            id: variant,
            quantity: this.quantity,
            properties: properties,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            selling_plan: sanitizedPlan
        }).then((item) => {
            if (itemInCart) {
                this.cartMain.updateCart('update', item, item.quantity);

                // document dispatch cartOpen event;
                if (!this.sideCart) { return; }
                this.sideCart.openCart();
            } else {
                this.cartMain.updateCart('add', item);
            }
        }).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        });

        this.loading = false;
    }

    private addStickyBannerEventListeners() {

        // Ensure sticky banner is rendered
        if (!this.stickyBanner) {
            return;
        }

        // Listen for scroll - handle sticky banner visibility
        this.$listen(window, 'scroll', throttle(() => this.handleScroll()));
        this.handleScroll();

        // Listen for edit button click
        this.listenForEditButtonClick();
    }

    private handleScroll() {

        // Determine element to watch scroll position
        const elementToWatch = this as HTMLElement;
        let additionalThreshold = 0;
        if (window.innerWidth <= this.mobileBreakpoint) {
            additionalThreshold = window.innerHeight;
        }

        // Get element top position
        const topPosition = elementToWatch.offsetTop;

        // Get element height
        const height = elementToWatch.offsetHeight;

        // Get menu height
        const menuHeight = this.siteMenu ? this.siteMenu.offsetHeight : 0;

        // Get footer height
        const footerHeight = this.siteFooter ? this.siteFooter.offsetHeight : 0;

        // Determine scroll threshold
        const scrollThreshold = topPosition + height - menuHeight;

        // Check if we have scrolled to the element
        if (window.pageYOffset >= scrollThreshold) {

            // Check if we have scrolled to the footer
            if (this.siteFooter) {
                const footerTopPosition = this.siteFooter.offsetTop;
                // Allowance on mobile to scroll half of foote height
                const mobFooterAllowance = window.innerWidth < 768 ? footerHeight / 2 : 0;
                const footerScrollThreshold = footerTopPosition - window.innerHeight + mobFooterAllowance;

                if (window.pageYOffset >= footerScrollThreshold) {
                    this.stickyBanner.classList.add('product-sticky--hidden');
                    document.body.classList.add('hide-rewards');
                    return;
                }

            }

            // Remove hidden class
            this.stickyBanner.classList.remove('product-sticky--hidden');
            document.body.classList.add('hide-rewards');
        } else {
            // Add hidden class
            this.stickyBanner.classList.add('product-sticky--hidden');
            document.body.classList.remove('hide-rewards');
        }
    }

    private listenForEditButtonClick() {

        // Ensure edit button is rendered
        if (!this.editButton) {
            return;
        }

        //Listen for click, scroll to element
        this.$listen(this.editButton, 'click', () => {
            scrollToElement(this as HTMLElement);
        });
    }

}
